import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getModuleChatConfig, useActiveModule } from "@/modules";
import { useIsDesktop } from "@/hooks/use-is-desktop";
import useAuthStore from "@/store/use-auth-store";
import type { ChatAttachment } from "@/features/chat";
import {
  createDraftChatKey,
  isDraftChatKey,
  useStreamingStore,
} from "@/features/chat/store/streaming-store";
import { useLLMSelection } from "@/features/llm-selection";
import { useToast } from "@/features/toast";
import { useTranslation } from "react-i18next";
import { useChatDockStore } from "../store/chat-dock-store";

/**
 * Dock'un tek karar noktası — bileşenler prop almaz, buradan okur.
 *
 * Dock yalnızca aktif bir modülde, o modülün blueprint'i chat'i açmışsa ve
 * kullanıcı oturum açmışsa mevcuttur. Shared sayfalarda (cart, orders,
 * settings) ve chat sayfasının kendisinde görünmez. Desktop `isOpen`
 * tercihi saklanır; mobilde sheet her girişte kapalı başlar.
 *
 * Konuşma modül başına saklanır: Fashion'da başlayan sohbet, Models'a geçip
 * geri dönüldüğünde kaldığı yerden devam eder.
 */
export const useChatDock = () => {
  const navigate = useNavigate();
  const module = useActiveModule();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());
  const isDesktop = useIsDesktop();
  const {
    isOpen: preferredOpen,
    mobileOpen,
    open,
    close,
    chatByModule,
    setModuleChat,
    pending,
    setPending,
  } = useChatDockStore();
  const { t } = useTranslation();
  const { error: showError } = useToast();
  const { selectedModel } = useLLMSelection();

  // İlk boyamada SSR snapshot desktop sayar; persist açıkken mobil sheet
  // bir kare görünmesin diye viewport oturana kadar kapalı tutulur.
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => setHydrated(true), []);

  const preferred = isDesktop ? preferredOpen : mobileOpen;

  const chat = useMemo(() => getModuleChatConfig(module), [module]);

  const isAvailable = Boolean(module && chat?.enabled && isAuthenticated);
  const moduleId = module?.id;
  const chatId = moduleId ? (chatByModule[moduleId] ?? null) : null;

  // Bu modülün konuşması arka planda akıyor / okunmamış yanıtı var mı?
  // (Dock kapalıyken tutamaçtaki rozet; stream store'da yaşadığı için panel
  // unmount olsa da devam eder.)
  const isStreaming = useStreamingStore((s) =>
    chatId ? s.sessionsByChat[chatId]?.status === "streaming" : false,
  );
  const hasUnread = useStreamingStore((s) =>
    chatId ? Boolean(s.sessionsByChat[chatId]?.hasUnread) : false,
  );

  /** İlk mesaj: taslak oturumu dock'a bağlar. Id, cevap başarıyla bitince gelir. */
  const startConversation = useCallback(
    async (message: string, attachments?: ChatAttachment[]) => {
      if (!moduleId) return false;
      if (!selectedModel) {
        showError(t("error.noModelSelected"), { title: t("error.title") });
        return false;
      }
      const draftKey = createDraftChatKey();
      setPending({ chatId: draftKey, message, attachments });
      setModuleChat(moduleId, draftKey);
      return true;
    },
    [moduleId, selectedModel, setPending, setModuleChat, showError, t],
  );

  /**
   * Bu modülün konuşmasını bırak; boş composer'a dön. Konuşma silinmez.
   * Cevap bekleyen taslak iptal edilmez: arka planda biter ve listede görünür.
   */
  const newConversation = useCallback(() => {
    if (!moduleId) return;
    setModuleChat(moduleId, null);
  }, [moduleId, setModuleChat]);

  /** Aynı konuşmayı tam sayfada aç. Taslakta henüz id yoktur. */
  const expand = useCallback(() => {
    if (chatId && !isDraftChatKey(chatId)) navigate(`/chat/${chatId}`);
  }, [chatId, navigate]);

  const consumePending = useCallback(() => setPending(null), [setPending]);

  // Persist desktop tercihi mobilde sheet'i açmasın; toggle efektif duruma bakılır
  const toggle = useCallback(() => {
    if (preferred) close();
    else open();
  }, [preferred, close, open]);

  return {
    module,
    agentId: chat?.agentId,
    isAvailable,
    isOpen: hydrated && isAvailable && preferred,
    open,
    close,
    toggle,

    chatId,
    isStreaming,
    hasUnread,
    pending,
    consumePending,
    isCreating: false,
    startConversation,
    newConversation,
    expand,
  };
};
